import { useCallback } from "react";
import { v4 as uuid } from "uuid";

const flowIdToAbortController = new Map<string, AbortController>();
const issueToFlowIds = new Map<string, Set<string>>();

class AbortError extends Error {
  constructor(message = "Operation was aborted.") {
    super(message);
    this.name = "AbortError";
  }
}

export const useSnapshotAbortController = () => {
  // Get the AbortController for a given flow
  const getAbortController = useCallback((flowId: string) => {
    return flowIdToAbortController.get(flowId);
  }, []);

  // Get a unique ID which represents a set of requests for a given issue
  // This is used to abort all requests for a given issue
  const getFlowId = useCallback((issueId: string) => {
    const flowId = uuid();

    if (!issueToFlowIds.has(issueId)) {
      issueToFlowIds.set(issueId, new Set());
    }
    flowIdToAbortController.set(flowId, new AbortController());
    issueToFlowIds.get(issueId)?.add(flowId);

    return flowId;
  }, []);

  // Get the AbortSignal for a given flow
  const getSignal = useCallback(
    (flowId: string) => {
      return getAbortController(flowId)?.signal;
    },
    [getAbortController]
  );

  // Throw an AbortError if the flow was aborted
  const throwIfAborted = useCallback(
    (flowId: string) => {
      return getAbortController(flowId)?.signal.throwIfAborted();
    },
    [getAbortController]
  );

  // Check if the given error is an AbortError
  const isAbortError = useCallback((e: any) => {
    return typeof e === "object" && e?.name === "AbortError";
  }, []);

  // Abort all flows for a given issue, that were linked to the Issue by `getFlowId`
  const abortAllFlows = useCallback((issueId: string) => {
    const flowIds = issueToFlowIds.get(issueId);
    if (flowIds) {
      flowIds.forEach(flowId => {
        const abortController = flowIdToAbortController.get(flowId);
        if (abortController) {
          abortController.abort(new AbortError());
        }
      });
    }
  }, []);

  return {
    getFlowId,
    getSignal,
    throwIfAborted,
    abortAllFlows,
    isAbortError,
  };
};
